/**
 * Display helpers shared by the onboarding steps.
 */

/**
 * The running app's API key (client_id), resolved at runtime.
 *
 * Primary source is the `meta[name="shopify-api-key"]` tag the backend injects
 * per environment when it serves index.html — the same tag App Bridge itself
 * boots from, so it is authoritative for "which app is this". The un-replaced
 * `%SHOPIFY_API_KEY%` placeholder (Vite dev serves index.html verbatim) is
 * treated as absent, with App Bridge's own config as the fallback.
 */
export function getRuntimeApiKey(): string | null {
  const meta = document
    .querySelector('meta[name="shopify-api-key"]')
    ?.getAttribute('content')
    ?.trim();
  if (meta && !meta.startsWith('%')) return meta;
  const fromBridge = window.shopify?.config?.apiKey;
  return typeof fromBridge === 'string' && fromBridge.length > 0 ? fromBridge : null;
}

/**
 * Deep link into the theme editor with the try-on app block pre-added to the
 * product template — the merchant lands one Save away from an active widget.
 *
 * Shopify's app-block deep link is `addAppBlockId={api_key}/{block-handle}`
 * (shopify.dev → theme app extensions → configuration): the app's OWN
 * client_id plus the block's Liquid filename. A second staging E2E proved the
 * distinction the hard way — the theme extension uid used here previously
 * opened the editor onto a red "There is a problem with the app block" error.
 * The key is environment-specific (staging and production are different
 * apps), so it is derived from the runtime document, never hardcoded; without
 * it the link degrades to a dead `#` rather than a broken editor.
 */
export function getThemeEditorUrl(
  shop: string | null,
  apiKey: string | null = getRuntimeApiKey()
): string {
  if (!shop || !apiKey) return '#';
  return `https://${shop}/admin/themes/current/editor?template=product&addAppBlockId=${apiKey}/tryon-widget&target=mainSection`;
}

/**
 * A product price, in the shop's own currency, in the admin's locale.
 * The demo and the simulation both show the REAL price — never an invented
 * number — so a null price renders as nothing rather than a placeholder.
 */
export function formatPrice(
  price: string | null,
  currencyCode: string | null,
  locale: string
): string | null {
  if (price === null || price === '') return null;
  const amount = Number(price);
  if (!Number.isFinite(amount)) return null;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode ?? 'EUR',
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currencyCode ?? ''}`.trim();
  }
}

/** Map a demo failure reason to its localized message key lookup. */
import type { DemoFailureReason } from '../types';
import type { Translations } from '../i18n/types';

export function demoErrorMessage(t: Translations, reason: DemoFailureReason): string {
  const o = t.onboardingFlow;
  switch (reason) {
    case 'flagged':
      return o.demoErrorFlagged;
    case 'cannot_generate':
      return o.demoErrorCannotGenerate;
    case 'invalid_photo':
      return o.demoErrorInvalidPhoto;
    case 'payload_too_large':
      return o.demoErrorPayloadTooLarge;
    case 'image_unavailable':
      return o.demoErrorImageUnavailable;
    case 'engine_unavailable':
      return o.demoErrorEngineUnavailable;
    case 'timeout':
      return o.demoErrorTimeout;
    case 'allowance_exhausted':
    case 'attempts_exhausted':
      return o.demoErrorAllowanceExhausted;
    case 'too_soon':
      return o.demoErrorTooSoon;
    case 'product_not_found':
      return o.demoErrorProductNotFound;
    default:
      return o.demoErrorUnexpected;
  }
}
